import { useState, useMemo, useEffect, useCallback } from "react";
import { Lock, Users, Mail, Trash2, Check } from "lucide-react";
import type { SubscriptionTier } from "../billing/BillingPage";
import { InfoTooltip } from "../ui/InfoTooltip";
import { NumberInput } from "../ui/NumberInput";
import { useAllFeatureFlags } from "../../hooks/useAllFeatureFlags";
import { supabase, isSupabaseEnabled } from "../../lib/supabase";

export interface FundingSource {
  id: string;
  type: "equity_release" | "tax_refund" | "cash_buffer" | "contribution" | "other";
  label: string;
  monthlyAmount: number;
  annualAmount?: number;
  openingBalance?: number;
  drawdownMonthly?: number;
  frequency?: string;
  notes?: string;
  linkedPropertyId?: string;
  linkedEquityReleaseId?: string;
  derived?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AppSettings {
  lvrConservative: number; // percentage, default 70
  lvrStandard: number;     // percentage, default 80
  lvrAggressive: number;   // percentage, default 90
  lenderType: "major-bank" | "non-bank" | "non-conforming";
  /** When false, SMSF properties are excluded from portfolio totals across
   *  all pages. SMSF data is always visible in separate summary sections. */
  includeSMSFInPortfolio: boolean;
  /** Portfolio buffer % applied to total cost. Default 10. */
  portfolioBufferPercent?: number;
  /** Rent growth assumption for dynamic runway. Default 0.03 (3%). */
  rentGrowthAssumption?: number;
  /** Persisted funding sources (equity_release + tax_refund only).
   *  personal_contribution is computed from investment bucket at render time. */
  fundingSources?: FundingSource[];
  /** Whether the property portfolio bucket has been auto-seeded. Idempotency flag. */
  propertyBucketSeeded?: boolean;
  /** Expense buffer % — display-only on Personal Budget Grand Total. Default 10. Does NOT cascade to surplus or funding. */
  expenseBufferPercent?: number;
  /** Dashboard alert category IDs to suppress. Empty/undefined = all shown. */
  disabledAlertCategories?: string[];
  /** Email digest frequency. Default "weekly" (Monday mornings). */
  digestFrequency?: "daily" | "weekly" | "off";
}

const SETTINGS_KEY = "ppa:settings";

export function loadAppSettings(): AppSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Backward compat: old format had maxLVR, map it to lvrStandard
      if (parsed.maxLVR !== undefined && parsed.lvrStandard === undefined) {
        parsed.lvrStandard = parsed.maxLVR;
      }
      return { ...defaultSettings(), ...parsed };
    }
  } catch {}
  return defaultSettings();
}

export function saveAppSettings(s: AppSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}

function defaultSettings(): AppSettings {
  return { lvrConservative: 70, lvrStandard: 80, lvrAggressive: 90, lenderType: "major-bank", includeSMSFInPortfolio: true, portfolioBufferPercent: 10, rentGrowthAssumption: 0.03, fundingSources: [] };
}

const LENDER_OPTIONS: { value: AppSettings["lenderType"]; label: string }[] = [
  { value: "major-bank",      label: "Major bank (Big 4)" },
  { value: "non-bank",        label: "Non-bank lender" },
  { value: "non-conforming",  label: "Non-conforming lender" },
];

// ─── Definitions data ────────────────────────────────────────────────────────

const DEFINITIONS: { term: string; definition: string }[] = [
  {
    term: "ATO Schedule 7",
    definition:
      "The ATO's rental income and expense schedule, used when lodging your tax return. AusProp's Tax page generates a Schedule 7 equivalent summary.",
  },
  {
    term: "Borrowing Capacity",
    definition:
      "The maximum amount a lender will lend you, based on your income, expenses, existing debts and the lender's serviceability assessment rate.",
  },
  {
    term: "Capital Gains Tax (CGT)",
    definition:
      "Tax on profit when you sell a property. Held for more than 12 months = 50% CGT discount applies (for individuals and trusts). Companies and SMSFs have different treatment.",
  },
  {
    term: "Cost Base",
    definition:
      "The original purchase price of a property plus associated costs (stamp duty, legal fees, capital improvements). Used to calculate CGT at sale.",
  },
  {
    term: "Depreciation Schedule",
    definition:
      "A report from a quantity surveyor listing all depreciable items in your property and their annual decline in value. Used to reduce taxable rental income.",
  },
  {
    term: "Discretionary Trust",
    definition:
      "A trust where the trustee can distribute income to beneficiaries at their discretion, often used for tax planning and asset protection.",
  },
  {
    term: "Balancing Adjustment",
    definition:
      "When a Div 40 depreciating asset is disposed of (sold, scrapped, removed) — typically when you sell the property — ITAA s40-285 requires you to compare the asset's adjustable value (cost minus all decline claimed to date) to its termination value (the portion of sale proceeds you allocate to the asset). If termination > adjustable, the excess is income (recapture). If termination < adjustable, the shortfall is a deduction (residual decline). Blocked second-hand items don't trigger a balancing event — their unclaimed cost reduces CGT cost base instead.",
  },
  {
    term: "Div 40",
    definition:
      "Division 40 of tax law. Decline in value (depreciation) of plant and equipment in an investment property — appliances, carpets, blinds, hot water systems, etc. (distinct from Div 43 which covers the building structure). Two methods: prime cost (straight-line) or diminishing value (DV is double the PC rate, applied to a reducing balance). Effective life is set by ATO Tables 3-7 of TR 2024/3. Subject to the 9 May 2017 second-hand asset rule (s40-27): blocked for second-hand items in post-2017 residential acquisitions unless an exception applies (installed-by-owner, new-from-developer-6mo, excluded-entity, PPOR-converted). Items under $300 can be claimed in full in year of acquisition; items $300–$999 must enter the low-value pool.",
  },
  {
    term: "Excluded Entity",
    definition:
      "Under ITAA s40-27(2), a small group of entities are exempt from the second-hand asset rule. The four types are: companies (Pty Ltd), publicly listed unit trusts, managed investment trusts (MITs), and APRA-regulated super funds. Crucially, SMSFs are NOT excluded entities. Individuals, discretionary trusts, family trusts, and partnerships of individuals are also NOT excluded — they're subject to the standard second-hand block.",
  },
  {
    term: "Low-Value Pool",
    definition:
      "Under ITAA s40-425 / RPG p45, depreciating assets costing $300–$999 must be allocated to a low-value pool. The pool depreciates at 18.75% in the first year (half-rate, regardless of acquisition date) and 37.5% thereafter on the reducing balance. AusProp tracks the pool per property and walks the balance forward each FY automatically. On property disposal, AusProp simplifies by writing off the pool's closing balance fully in the disposal year (the alternative — continuing to depreciate the pool post-sale — is allowed but rarely useful for a solo investor).",
  },
  {
    term: "Second-hand Rule",
    definition:
      "ITAA s40-27 (effective from 9 May 2017): for residential rental properties acquired after that date, plant and equipment items that came with the property (i.e., were second-hand at acquisition) cannot be depreciated by the new owner. Four exceptions: items installed by the owner after acquisition, items acquired new from a developer within 6 months of completion, ownership by an excluded entity (s40-27(2) — companies, MITs, public unit trusts, APRA super), and PPOR-converted properties for items acquired during the PPOR period (s40-27(3)). Blocked items don't lose their tax value entirely — their cost flows to the property's CGT cost base on disposal (s110-25).",
  },
  {
    term: "Termination Value",
    definition:
      "The portion of sale proceeds you allocate to a specific Div 40 depreciating asset on disposal of the property. Used to compute the balancing adjustment per s40-285. If you don't allocate (default), the termination value is set equal to the asset's adjustable value, producing zero balancing adjustment. If your sale agreement allocates differently (e.g., extra value for a recently installed cooktop), you can override per-asset in the Mark-As-Sold modal.",
  },
  {
    term: "Div 43",
    definition:
      "Division 43 of tax law. Capital works depreciation on the building structure itself. Rate is keyed by the date construction commenced (not completed): 2.5% over 40 years for buildings commenced post-15-Sep-1987 (the standard case), 4% over 25 years for 1984–1987 buildings / eligible Build-to-Rent developments / certain hotels. AusProp derives the rate from your commenced date; you can override if your QS report disagrees.",
  },
  {
    term: "Entity",
    definition:
      "The legal structure that owns a property. Common entities: personal name, discretionary trust, unit trust, company, SMSF. Each has different tax and CGT treatment.",
  },
  {
    term: "Equity",
    definition:
      "The portion of your property you own outright. Calculated as current value minus outstanding loan balance.",
  },
  {
    term: "Gross Yield",
    definition:
      "Annual rental income divided by purchase price. A rough measure of a property's income return before expenses.",
  },
  {
    term: "Land Tax",
    definition:
      "A state government tax on the unimproved value of land you own (excluding your primary residence in most states). Thresholds and rates vary by state — trusts often have much lower thresholds.",
  },
  {
    term: "LVR",
    definition:
      "Loan-to-Value Ratio. Your total loan balance divided by your property's current value, expressed as a percentage. A lower LVR means more equity and less risk. Most lenders prefer below 80%.",
  },
  {
    term: "Negative Gearing",
    definition:
      "When your rental income is less than your expenses (including interest). The loss can offset your taxable income, reducing your tax bill.",
  },
  {
    term: "Net Yield",
    definition:
      "Annual rental income minus expenses, divided by purchase price. A more accurate measure of actual return than gross yield.",
  },
  {
    term: "PAYG",
    definition:
      "Pay As You Go. The system where your employer withholds income tax from your salary before you receive it.",
  },
  {
    term: "PAYG Withholding Variation",
    definition:
      "An ATO form (NAA 2036) allowing you to reduce tax withheld from your salary to account for rental losses throughout the year, rather than waiting for your tax return.",
  },
  {
    term: "Positive Gearing",
    definition:
      "When your rental income exceeds all expenses. The profit is added to your taxable income.",
  },
  {
    term: "Serviceability Rate",
    definition:
      "The interest rate lenders use to stress-test your ability to repay a loan. Typically 2–3% above the actual rate (APRA's buffer is currently 3%).",
  },
  {
    term: "Settlement Date",
    definition:
      "The date ownership of the property legally transfers to you. For CGT purposes, the relevant date is the contract date, not settlement.",
  },
  {
    term: "SMSF",
    definition:
      "Self-Managed Super Fund. A private superannuation fund you control. Can hold investment property under strict ATO rules, including the Limited Recourse Borrowing Arrangement (LRBA).",
  },
  {
    term: "Stamp Duty",
    definition:
      "A state government tax paid when purchasing property. Calculated as a percentage of purchase price, varies by state and whether you're a first home buyer.",
  },
  {
    term: "Usable Equity",
    definition:
      "The equity you can access for a new purchase. Typically calculated as (Value × max LVR%) minus current debt. Example: at 80% LVR on a $1M property with $600k debt: usable = $800k − $600k = $200k.",
  },
];

// ─── props ────────────────────────────────────────────────────���───────────────

const ADMIN_EMAIL = "anguswilliams76@gmail.com";

interface SettingsPageProps {
  settings: AppSettings;
  onSave: (s: AppSettings) => void;
  subscriptionTier?: SubscriptionTier;
  onOpenImport?: () => void;
  onNavigateToBilling?: () => void;
  userEmail?: string;
  userId?: string;
  userName?: string;
  onDeleteProperties?: () => Promise<void>;
  onDeleteBudget?: () => Promise<void>;
  onDeleteProfile?: () => Promise<void>;
  hasFullAccess?: boolean;
}

type Tab = "general" | "definitions" | "security" | "sharing" | "admin";

// ─── Danger Zone ─────────────────────────────────────────────────────────────

interface DangerZoneProps {
  onDeleteProperties?: () => Promise<void>;
  onDeleteBudget?: () => Promise<void>;
  onDeleteProfile?: () => Promise<void>;
}

function DangerZone({ onDeleteProperties, onDeleteBudget, onDeleteProfile }: DangerZoneProps) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleted, setDeleted] = useState<string | null>(null);

  async function handleConfirm(key: string, fn: (() => Promise<void>) | undefined) {
    if (!fn) return;
    await fn();
    setConfirming(null);
    setDeleted(key);
    setTimeout(() => setDeleted(null), 2500);
  }

  const rows: { key: string; label: string; description: string; fn?: () => Promise<void> }[] = [
    {
      key: "properties",
      label: "Delete all properties",
      description: "Removes all properties, loans and structures from your portfolio.",
      fn: onDeleteProperties,
    },
    {
      key: "budget",
      label: "Delete budget",
      description: "Resets your Personal Budget to the default template.",
      fn: onDeleteBudget,
    },
    {
      key: "profile",
      label: "Delete profile",
      description: "Clears your My Profile information including income and tax settings.",
      fn: onDeleteProfile,
    },
  ];

  return (
    <div className="rounded-2xl border border-red-100 bg-white p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold" style={{ color: "#DC2626" }}>⚠ Danger Zone</h2>
        <p className="mt-1 text-xs text-neutral-400">These actions are permanent and cannot be undone.</p>
      </div>
      <div className="space-y-3">
        {rows.map(({ key, label, description, fn }) => {
          if (!fn) return null;
          return (
            <div key={key} className="rounded-xl border border-neutral-200 border-l-4 border-l-red-500 bg-white p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900">{label}</p>
                  <p className="mt-0.5 text-sm text-neutral-500">{description}</p>
                </div>
                {confirming !== key && deleted !== key && (
                  <button
                    onClick={() => setConfirming(key)}
                    className="shrink-0 rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                  >
                    Delete
                  </button>
                )}
                {deleted === key && (
                  <span className="shrink-0 text-xs font-medium text-emerald-600">Deleted ✓</span>
                )}
              </div>
              {confirming === key && (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="text-xs font-medium text-amber-700">Are you sure? This cannot be undone.</span>
                  <button
                    onClick={() => void handleConfirm(key, fn)}
                    className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 transition-colors"
                  >
                    Yes, delete
                  </button>
                  <button
                    onClick={() => setConfirming(null)}
                    className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ReferralSection ─────────────────────────────────────────────────────────

interface ReferralRow {
  referee_email: string | null;
  status: string;
  created_at: string;
}

function ReferralSection({ userId }: { userId?: string }) {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referrals, setReferrals] = useState<ReferralRow[]>([]);
  const [copied, setCopied] = useState(false);
  // Init from whether a load will actually run, so the effect needn't do a
  // synchronous setLoading(false) in its early-return. (The referrals UI only
  // renders when userId && isSupabaseEnabled, so the no-user case is moot.)
  const [loading, setLoading] = useState<boolean>(() => !!(userId && isSupabaseEnabled() && supabase));

  useEffect(() => {
    if (!userId || !isSupabaseEnabled() || !supabase) return;

    async function load() {
      if (!supabase || !userId) return;
      setLoading(true); // ensure the spinner shows even if userId resolved after mount
      const { data: profile } = await supabase
        .from("user_profiles")
        .select("referral_code")
        .eq("id", userId)
        .maybeSingle();
      if (profile?.referral_code) setReferralCode(profile.referral_code as string);

      const { data: rows } = await supabase
        .from("referrals")
        .select("referee_email, status, created_at")
        .eq("referrer_user_id", userId)
        .order("created_at", { ascending: false });
      if (rows) setReferrals(rows as ReferralRow[]);
      setLoading(false);
    }
    void load();
  }, [userId]);

  if (!userId || !isSupabaseEnabled()) return null;

  const referralLink = referralCode ? `https://ausprop.app?ref=${referralCode}` : null;

  const statusBadge = (status: string) => {
    if (status === "rewarded") return <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Rewarded</span>;
    if (status === "subscribed") return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">Subscribed</span>;
    return <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-500">Pending</span>;
  };

  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4">
      <div>
        <h2 className="text-base font-semibold text-neutral-800">Refer a Friend</h2>
        <p className="mt-1 text-xs text-neutral-400">
          Share your unique link. When a friend subscribes to the Investor plan, you get 1 month free.
        </p>
      </div>

      {loading ? (
        <div className="h-8 w-48 rounded bg-neutral-100 animate-pulse" />
      ) : referralLink ? (
        <>
          <div className="space-y-2">
            <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Your referral link</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 rounded-lg bg-neutral-50 border border-neutral-200 px-3 py-2 text-xs font-mono text-neutral-700 truncate select-all">
                {referralLink}
              </div>
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(referralLink);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="shrink-0 rounded-lg bg-neutral-900 px-3 py-2 text-xs font-medium text-white hover:bg-neutral-700 transition-colors"
              >
                {copied ? "Copied ✓" : "Copy"}
              </button>
            </div>
            <p className="text-xs text-neutral-400">
              Code: <span className="font-mono font-semibold text-neutral-600">{referralCode}</span>
            </p>
          </div>

          {referrals.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Referral history</p>
              <div className="rounded-xl border border-neutral-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-100 bg-neutral-50">
                      <th className="py-2 px-3 text-left text-xs font-medium text-neutral-500">Friend</th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-neutral-500">Date</th>
                      <th className="py-2 px-3 text-right text-xs font-medium text-neutral-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((r, i) => (
                      <tr key={i} className="border-b border-neutral-50 last:border-0">
                        <td className="py-2.5 px-3 text-xs text-neutral-700 font-mono">
                          {r.referee_email ?? "—"}
                        </td>
                        <td className="py-2.5 px-3 text-xs text-neutral-400">
                          {new Date(r.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                        </td>
                        <td className="py-2.5 px-3 text-right">
                          {statusBadge(r.status)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-xs text-neutral-400">No referrals yet. Share your link to get started.</p>
          )}
        </>
      ) : (
        <p className="text-xs text-neutral-400">Sign in to see your referral link.</p>
      )}
    </div>
  );
}

// ─── component ────────────────────────────────────────────────────────────────

export default function SettingsPage({ settings, onSave, subscriptionTier: _subscriptionTier, onOpenImport, onNavigateToBilling, userEmail, userId, userName, onDeleteProperties, onDeleteBudget, onDeleteProfile, hasFullAccess = false }: SettingsPageProps) {
  const [draft, setDraft] = useState<AppSettings>(settings);
  const [saved, setSaved] = useState(false);
  const [tab, setTab] = useState<Tab>("general");
  const [defSearch, setDefSearch] = useState("");

  function handleSave() {
    onSave(draft);
    saveAppSettings(draft);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  const filteredDefs = useMemo(() => {
    const q = defSearch.toLowerCase().trim();
    if (!q) return DEFINITIONS;
    return DEFINITIONS.filter(
      (d) =>
        d.term.toLowerCase().includes(q) ||
        d.definition.toLowerCase().includes(q)
    );
  }, [defSearch]);

  // Group filtered defs by first letter
  const grouped = useMemo(() => {
    const map: Record<string, typeof DEFINITIONS> = {};
    for (const d of filteredDefs) {
      const letter = d.term[0].toUpperCase();
      if (!map[letter]) map[letter] = [];
      map[letter].push(d);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredDefs]);

  const isAdmin = userEmail === ADMIN_EMAIL;

  const tabs: { id: Tab; label: string }[] = [
    { id: "general", label: "General" },
    { id: "definitions", label: "Definitions" },
    { id: "security", label: "Security & Privacy" },
    { id: "sharing", label: "Sharing" },
    ...(isAdmin ? [{ id: "admin" as Tab, label: "Admin" }] : []),
  ];

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-neutral-900">Settings</h1>
        <p className="mt-1 text-sm text-neutral-500">App configuration, glossary and privacy.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-neutral-200">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t.id
                ? "border-[#C47A2F] text-[#C47A2F]"
                : "border-transparent text-neutral-500 hover:text-neutral-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── GENERAL TAB ───────────��─────────────────���───────────────────────── */}
      {tab === "general" && (
        <div className="space-y-8">
          {/* Borrowing Assumptions */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-neutral-800">LVR Presets</h2>
              <p className="mt-1 text-xs text-neutral-400">
                Three LVR scenarios used across Strategy, Dashboard, and Simulator. Standard is used as the default for equity calculations.
              </p>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {(["lvrConservative", "lvrStandard", "lvrAggressive"] as const).map((key) => {
                const labels: Record<typeof key, string> = {
                  lvrConservative: "Conservative",
                  lvrStandard: "Standard",
                  lvrAggressive: "Aggressive",
                };
                const tips: Record<typeof key, string> = {
                  lvrConservative: "Lower risk — suitable for conservative lenders or consolidation phase.",
                  lvrStandard: "Default for Strategy and equity calculations. Typical major-bank maximum.",
                  lvrAggressive: "Higher leverage — models non-conforming or non-bank lending scenarios.",
                };
                return (
                  <div key={key}>
                    <div className="flex items-center gap-1 mb-1">
                      <label className="block text-xs font-medium text-neutral-700">{labels[key]}</label>
                      <InfoTooltip tip={tips[key]} />
                    </div>
                    <div className="flex items-center gap-1">
                      <NumberInput
                        min={50}
                        max={97}
                        step={5}
                        value={draft[key]}
                        onChange={(e) => {
                          const v = Math.min(97, Math.max(50, parseInt(e.target.value) || 80));
                          setDraft((d) => ({ ...d, [key]: v }));
                        }}
                        className="w-20 rounded-lg border border-neutral-200 px-2 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                      />
                      <span className="text-xs text-neutral-500">%</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Lender type
              </label>
              <select
                value={draft.lenderType}
                onChange={(e) => setDraft((d) => ({ ...d, lenderType: e.target.value as AppSettings["lenderType"] }))}
                className="rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-300"
              >
                {LENDER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-neutral-400">
                For informational context — does not affect calculations in Phase 1.
              </p>
            </div>

            <div className="pt-2">
              <button
                onClick={handleSave}
                className="rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 transition-colors"
              >
                {saved ? "Saved ✓" : "Save settings"}
              </button>
            </div>
          </div>

          {/* Portfolio Display */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-5">
            <div>
              <h2 className="text-base font-semibold text-neutral-800">Portfolio Display</h2>
              <p className="mt-1 text-xs text-neutral-400">
                Control how SMSF properties are included in your portfolio totals.
              </p>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <div className="relative mt-0.5">
                <input
                  type="checkbox"
                  checked={draft.includeSMSFInPortfolio}
                  onChange={(e) => {
                    const next = { ...draft, includeSMSFInPortfolio: e.target.checked };
                    setDraft(next);
                    onSave(next);
                    saveAppSettings(next);
                    setSaved(true);
                    setTimeout(() => setSaved(false), 2000);
                  }}
                  className="peer sr-only"
                />
                <div className="h-5 w-9 rounded-full bg-neutral-200 peer-checked:bg-emerald-500 transition-colors" />
                <div className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
              </div>
              <div>
                <span className="text-sm font-medium text-neutral-800">Include SMSF in portfolio totals</span>
                <p className="text-xs text-neutral-400 mt-0.5">
                  When off, SMSF properties are excluded from portfolio value, debt, equity, cashflow, and interest totals across all pages. SMSF data is always shown in a separate summary section.
                </p>
              </div>
            </label>
          </div>

          {/* Dashboard Alerts */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4">
            <div>
              <h2 className="flex items-center gap-1 text-base font-semibold text-neutral-800">
                Dashboard Alerts
                <InfoTooltip tip="Priority actions on the Dashboard use three severity levels: red = urgent (IO expiry, high LVR), amber = attention (fixed rate, trust deadline, high top-up, lease), green = opportunity (usable equity). Disabling a category hides it from the Dashboard but the underlying data is still tracked." />
              </h2>
              <p className="mt-1 text-xs text-neutral-400">
                Choose which alerts appear as priority actions on your Dashboard. Disabled alerts won't surface but you can re-enable any time.
              </p>
            </div>
            <div className="mb-4 pb-4 border-b border-neutral-100">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Email digest</label>
              <p className="text-xs text-neutral-500 mb-2">Get an email summary of upcoming reminders. Disabled categories below are also excluded from the digest.</p>
              <select
                value={draft.digestFrequency ?? "weekly"}
                onChange={(e) => {
                  const next = { ...draft, digestFrequency: e.target.value as "daily" | "weekly" | "off" };
                  setDraft(next); onSave(next); saveAppSettings(next);
                  setSaved(true); setTimeout(() => setSaved(false), 2000);
                }}
                className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
              >
                <option value="off">Off — no emails</option>
                <option value="weekly">Weekly — Monday mornings</option>
                <option value="daily">Daily — 7-8am</option>
              </select>
              <p className="text-xs text-neutral-400 mt-1">Times are approximate (7-8am AEST depending on daylight saving).</p>
            </div>
            <div className="space-y-1">
              {([
                { id: "io-expiry", label: "IO period ending", description: "Alert when an interest-only loan period expires within 12 months" },
                { id: "fixed-rate-expiry", label: "Fixed rate expiring", description: "Alert when a fixed-rate loan period expires within 12 months" },
                { id: "lvr-high", label: "Portfolio LVR above 80%", description: "Alert when overall loan-to-value ratio exceeds 80%" },
                { id: "trust-deadline", label: "Trust resolution deadline", description: "Reminder when 31 August deadline approaches for trust distributions" },
                { id: "high-topup", label: "High monthly top-up required", description: "Alert when portfolio shortfall exceeds $3,000/month" },
                { id: "equity-available", label: "Usable equity available", description: "Notify when significant usable equity is available for investment" },
                { id: "lease-expiry", label: "Commercial lease expiring", description: "Alert when commercial/industrial lease ends within 6 months" },
                { id: "rent-review", label: "Rent review due", description: "Notify when a property's next rent review is approaching" },
                { id: "smoke-alarm", label: "Smoke alarm cert expiring", description: "Notify when a smoke alarm certification is approaching expiry" },
                { id: "pool-safety", label: "Pool safety cert expiring", description: "Notify when a pool safety certificate is approaching expiry" },
                { id: "council-rates", label: "Council rates due", description: "Notify when a council rates instalment is approaching" },
                { id: "water-rates", label: "Water rates due", description: "Notify when a water rates instalment is approaching" },
                { id: "strata-levy", label: "Strata levy due", description: "Notify when a strata levy is approaching" },
                { id: "valuation-stale", label: "Bank valuation stale", description: "Notify when a property's bank valuation is older than 2 years" },
              ] as const).map((cat) => {
                const disabled = draft.disabledAlertCategories ?? [];
                const isEnabled = !disabled.includes(cat.id);
                return (
                  <label key={cat.id} className="flex items-start gap-3 py-2 cursor-pointer hover:bg-neutral-50 rounded px-2">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => {
                        const next = {
                          ...draft,
                          disabledAlertCategories: e.target.checked
                            ? disabled.filter((id) => id !== cat.id)
                            : [...disabled, cat.id],
                        };
                        setDraft(next);
                        onSave(next);
                        saveAppSettings(next);
                        setSaved(true);
                        setTimeout(() => setSaved(false), 2000);
                      }}
                      className="mt-0.5 rounded border-neutral-300"
                    />
                    <div>
                      <div className="text-sm font-medium text-neutral-800">{cat.label}</div>
                      <div className="text-xs text-neutral-400">{cat.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Import / Export */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4">
            <div>
              <h2 className="text-base font-semibold text-neutral-800">Import / Export</h2>
              <p className="mt-0.5 text-xs text-neutral-400">Import your existing portfolio data from a spreadsheet, CSV, or PDF.</p>
            </div>

            {hasFullAccess ? (
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm text-neutral-700 font-medium">Import portfolio data</p>
                  <p className="mt-0.5 text-xs text-neutral-400">Upload a spreadsheet or paste data — AI extracts every field it can find.</p>
                </div>
                <button
                  onClick={onOpenImport}
                  className="shrink-0 rounded-lg bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-700 transition-colors"
                >
                  Open import wizard
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-amber-100 bg-amber-50 px-4 py-3 flex items-center justify-between gap-4">
                <p className="text-sm text-amber-800">
                  Subscribe to access the import wizard.
                </p>
                <button
                  onClick={onNavigateToBilling}
                  className="shrink-0 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50 transition-colors"
                >
                  Upgrade →
                </button>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-neutral-700 font-medium">Export to CSV</p>
                <p className="mt-0.5 text-xs text-neutral-400">All properties and expenses in a single file for your accountant.</p>
              </div>
              <span className="text-xs text-neutral-400">Coming soon</span>
            </div>
          </div>

          {/* Referral */}
          <ReferralSection userId={userId} />

          {/* Help & Support */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">Help &amp; Support</h2>
            <p className="text-sm text-neutral-600">
              For help, feedback or complaints:{" "}
              <a href="mailto:propertyoshq@gmail.com" className="text-neutral-900 underline hover:text-neutral-600">
                propertyoshq@gmail.com
              </a>
            </p>
            <div className="flex gap-4">
              <a href="/terms" className="text-sm text-neutral-500 underline hover:text-neutral-800">
                Terms of Service
              </a>
              <a href="/privacy" className="text-sm text-neutral-500 underline hover:text-neutral-800">
                Privacy Policy
              </a>
            </div>
          </div>

          {/* Data Partners */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">Data Partners</h2>
            <a
              href="https://developer.htagai.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 hover:border-amber-300 hover:bg-amber-50 transition-colors"
            >
              <span className="text-xs text-neutral-500">Market Intelligence</span>
              <span className="text-sm font-semibold text-amber-700">HtAG Analytics</span>
            </a>
            <p className="text-xs text-neutral-400">
              Suburb market data, growth cycles, and investment signals powered by HtAG Analytics — Australia's leading property intelligence platform.
            </p>
          </div>

          {/* About AusProp */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">About AusProp</h2>
            <div className="space-y-1.5 text-sm text-neutral-600">
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Version</span>
                <span className="font-mono text-xs bg-neutral-100 rounded px-2 py-0.5">v1.0.0</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Build</span>
                <span className="font-mono text-xs bg-neutral-100 rounded px-2 py-0.5">{__COMMIT_HASH__}</span>
              </div>
            </div>
            <div className="flex gap-4 pt-1">
              <a
                href="https://github.com/propertyoshq-dot/property-portfolio-app/commits/main"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-neutral-400 underline hover:text-neutral-700"
              >
                View changelog ↗
              </a>
              <span className="text-xs text-neutral-300">·</span>
              <span className="text-xs text-neutral-400">
                To roll back, visit Vercel Dashboard → Deployments → promote previous build
              </span>
            </div>
          </div>

          <DangerZone
            onDeleteProperties={onDeleteProperties}
            onDeleteBudget={onDeleteBudget}
            onDeleteProfile={onDeleteProfile}
          />

          <p className="text-xs text-neutral-400">Settings are stored locally in your browser. They will persist until you clear your browser data.</p>
        </div>
      )}

      {/* ── DEFINITIONS TAB ───────────────────────────────��──────────────────── */}
      {tab === "definitions" && (
        <div className="space-y-6">
          <div>
            <p className="text-sm text-neutral-500 mb-4">
              Plain English definitions of every key term used in AusProp — including the exact formulas.
            </p>
            <input
              type="text"
              placeholder="Search definitions…"
              value={defSearch}
              onChange={(e) => setDefSearch(e.target.value)}
              className="w-full rounded-xl border border-neutral-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300"
            />
          </div>

          {grouped.length === 0 && (
            <p className="text-sm text-neutral-400 py-4 text-center">No definitions match "{defSearch}".</p>
          )}

          {grouped.map(([letter, defs]) => (
            <div key={letter}>
              <div className="text-xs font-bold text-neutral-400 tracking-widest uppercase mb-3 pb-1 border-b border-neutral-100">
                {letter}
              </div>
              <div className="space-y-4">
                {defs.map((d) => (
                  <div key={d.term}>
                    <p className="text-sm font-semibold text-neutral-900">{d.term}</p>
                    <p className="text-sm text-neutral-500 mt-0.5 leading-relaxed">{d.definition}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── SECURITY & PRIVACY TAB ───────────────────────────────────────────── */}
      {tab === "security" && (
        <div className="space-y-6">
          {/* How we store your data */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-neutral-100">
                <Lock size={18} className="text-neutral-600" />
              </div>
              <h2 className="text-base font-semibold text-neutral-800">How we store your data</h2>
            </div>

            <ul className="space-y-3">
              {[
                "Your data is encrypted at rest using AES-256 in Supabase (Postgres on AWS infrastructure).",
                "Row-level security means only your account can access your data — AusProp staff cannot see your data.",
                "Supabase is SOC 2 Type II certified.",
                "Your data is never sold, shared, or used for any purpose other than running your account.",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                  <span className="text-sm text-neutral-700 leading-relaxed">{point}</span>
                </li>
              ))}
            </ul>

            <div className="pt-1 flex gap-4">
              <a href="/privacy" className="text-sm text-neutral-500 underline hover:text-neutral-800">
                Privacy Policy
              </a>
              <a href="/terms" className="text-sm text-neutral-500 underline hover:text-neutral-800">
                Terms of Service
              </a>
            </div>
          </div>

          {/* AI & data processing */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-3">
            <h2 className="text-base font-semibold text-neutral-800">AI &amp; data processing</h2>
            <p className="text-sm text-neutral-600 leading-relaxed">
              When you use AI features (AI chat, import wizard, Strategy narrative), your portfolio data is sent
              to Anthropic's API to generate responses. This data is <strong>not stored by Anthropic</strong> and
              is <strong>not used to train AI models</strong>. Anthropic processes requests and returns a response —
              nothing is retained.
            </p>
            <p className="text-sm text-neutral-600 leading-relaxed">
              AI calls are proxied through AusProp's secure server-side infrastructure — your data never passes
              through your browser to Anthropic directly.
            </p>
            <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="text-sm text-neutral-500 underline hover:text-neutral-800">
              Anthropic Privacy Policy ↗
            </a>
          </div>

          {/* Contact */}
          <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-2">
            <h2 className="text-base font-semibold text-neutral-800">Data requests</h2>
            <p className="text-sm text-neutral-600">
              To request access to, correction of, or deletion of your data, email:{" "}
              <a href="mailto:propertyoshq@gmail.com" className="text-neutral-900 underline hover:text-neutral-600">
                propertyoshq@gmail.com
              </a>
            </p>
          </div>
        </div>
      )}

      {/* ── SHARING TAB ───────────────────────────────────────────────────────── */}
      {tab === "sharing" && (
        <SharingPanel userId={userId} userEmail={userEmail} userName={userName} />
      )}

      {/* ── ADMIN TAB ─────────────────────────────────────────────────────────── */}
      {tab === "admin" && isAdmin && <AdminFlagsPanel />}
    </div>
  );
}

// ─── Sharing Panel ────────────────────────────────────────────────────────────

interface PortfolioShare {
  id: string;
  shared_with_email: string;
  role: "viewer" | "co_owner";
  status: "pending" | "accepted" | "revoked";
  created_at: string;
}

function SharingPanel({ userId, userEmail: _userEmail, userName }: { userId?: string; userEmail?: string; userName?: string }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"viewer" | "co_owner">("viewer");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [shares, setShares] = useState<PortfolioShare[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const loadShares = useCallback(async () => {
    if (!isSupabaseEnabled() || !supabase || !userId) return;
    setLoadingShares(true);
    const { data, error } = await supabase
      .from("portfolio_shares")
      .select("id, shared_with_email, role, status, created_at")
      .eq("owner_user_id", userId)
      .neq("status", "revoked")
      .order("created_at", { ascending: false });
    setLoadingShares(false);
    if (!error && data) setShares(data as PortfolioShare[]);
  }, [userId]);

  useEffect(() => {
    void loadShares();
  }, [loadShares]);

  function isValidEmail(e: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());
  }

  async function handleSendInvite() {
    if (!userId || !isValidEmail(email)) return;
    setSending(true);
    setSendResult(null);
    try {
      if (!isSupabaseEnabled() || !supabase) throw new Error("Supabase not enabled");
      const { data: { session } } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke("portfolio-invite", {
        body: {
          ownerUserId: userId,
          sharedWithEmail: email.trim().toLowerCase(),
          role,
          ownerName: userName ?? session?.user?.email ?? "An AusProp user",
        },
      });
      if (res.error) throw res.error;
      setSendResult({ ok: true, msg: `Invite sent to ${email.trim()}` });
      setEmail("");
      void loadShares();
    } catch (err) {
      setSendResult({ ok: false, msg: "Failed to send invite — please try again." });
      console.error("[SharingPanel] invite error:", err);
    }
    setSending(false);
  }

  async function handleRevoke(shareId: string) {
    if (!isSupabaseEnabled() || !supabase) return;
    setRevokingId(shareId);
    const { error } = await supabase
      .from("portfolio_shares")
      .update({ status: "revoked" })
      .eq("id", shareId);
    setRevokingId(null);
    setConfirmRevokeId(null);
    if (!error) void loadShares();
  }

  const roleBadge = (r: "viewer" | "co_owner") =>
    r === "co_owner"
      ? <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-700">Co-owner</span>
      : <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">View only</span>;

  const statusBadge = (s: "pending" | "accepted" | "revoked") => {
    if (s === "accepted") return <span className="flex items-center gap-1 text-xs font-medium text-emerald-600"><Check size={12} />Accepted</span>;
    if (s === "pending") return <span className="text-xs font-medium text-amber-600">Pending</span>;
    return <span className="text-xs text-neutral-400">Revoked</span>;
  };

  if (!isSupabaseEnabled()) {
    return (
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500">
        Portfolio sharing requires a Supabase connection.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Invite form */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-neutral-100">
            <Users size={18} className="text-neutral-600" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-neutral-800">Share your portfolio</h2>
            <p className="text-xs text-neutral-400 mt-0.5">Invite your accountant or co-owner to view your properties, loans, and tax data.</p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Email address</label>
            <div className="relative">
              <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setSendResult(null); }}
                placeholder="accountant@example.com"
                className="w-full rounded-lg border border-neutral-200 pl-9 pr-3 py-2.5 text-sm text-neutral-900 placeholder-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-300"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-neutral-700 mb-1">Access level</label>
            <div className="flex gap-3">
              {(["viewer", "co_owner"] as const).map((r) => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors text-left ${
                    role === r
                      ? "border-[#C47A2F] bg-[#FDF3E4] text-[#92520A]"
                      : "border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50"
                  }`}
                >
                  <span className="block text-sm font-semibold">{r === "viewer" ? "View only" : "Co-owner"}</span>
                  <span className="block text-xs font-normal mt-0.5 text-neutral-500">
                    {r === "viewer" ? "Can see all data, cannot edit" : "Can see all data and loans"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={() => void handleSendInvite()}
            disabled={sending || !isValidEmail(email)}
            className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-50"
            style={{ background: "#C47A2F" }}
          >
            {sending ? "Sending…" : "Send invite"}
          </button>

          {sendResult && (
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm ${sendResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
              {sendResult.ok && <Check size={14} />}
              {sendResult.msg}
            </div>
          )}
        </div>
      </div>

      {/* Active shares list */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4">
        <h2 className="text-base font-semibold text-neutral-800">Active shares</h2>

        {loadingShares && (
          <div className="flex items-center gap-2 py-4 text-sm text-neutral-400">
            <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
            </svg>
            Loading…
          </div>
        )}

        {!loadingShares && shares.length === 0 && (
          <p className="text-sm text-neutral-400 py-2">No active shares. Invite someone above to get started.</p>
        )}

        {!loadingShares && shares.length > 0 && (
          <div className="space-y-2">
            {shares.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-4 rounded-xl border border-neutral-100 bg-neutral-50 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neutral-900 truncate">{s.shared_with_email}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {roleBadge(s.role)}
                    {statusBadge(s.status)}
                    <span className="text-xs text-neutral-400">
                      {new Date(s.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {confirmRevokeId === s.id ? (
                    <>
                      <button
                        onClick={() => void handleRevoke(s.id)}
                        disabled={revokingId === s.id}
                        className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                      >
                        {revokingId === s.id ? "Revoking…" : "Confirm"}
                      </button>
                      <button
                        onClick={() => setConfirmRevokeId(null)}
                        className="rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 hover:bg-neutral-100 transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setConfirmRevokeId(s.id)}
                      className="flex items-center gap-1.5 rounded-lg border border-neutral-200 px-3 py-1.5 text-xs font-medium text-neutral-600 hover:border-red-200 hover:text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={12} />
                      Revoke
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Admin: Feature Flags Panel ───────────────────────────────────────────────

interface FlagDraft {
  enabled: boolean;
  userEmailsText: string;
}
type SaveStatus = "idle" | "saving" | "saved" | "error";

function AdminFlagsPanel() {
  const { flags, loading, refresh } = useAllFeatureFlags();
  const [drafts, setDrafts] = useState<Record<string, FlagDraft>>({});
  const [statuses, setStatuses] = useState<Record<string, SaveStatus>>({});

  // Initialise drafts from flags once loaded
  const initialisedRef = useState(false);
  if (!initialisedRef[0] && flags.length > 0) {
    initialisedRef[1](true);
    const initial: Record<string, FlagDraft> = {};
    flags.forEach((f) => {
      initial[f.flag] = {
        enabled: f.enabled,
        userEmailsText: f.user_emails.join(", "),
      };
    });
    setDrafts(initial);
  }

  function getDraft(flag: string, fallback: { enabled: boolean; user_emails: string[] }): FlagDraft {
    return drafts[flag] ?? { enabled: fallback.enabled, userEmailsText: fallback.user_emails.join(", ") };
  }

  async function handleSave(flagName: string) {
    if (!isSupabaseEnabled() || !supabase) return;
    const draft = drafts[flagName];
    if (!draft) return;
    setStatuses((s) => ({ ...s, [flagName]: "saving" }));
    const userEmails = draft.userEmailsText
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    const { error } = await supabase
      .from("feature_flags")
      .update({ enabled: draft.enabled, user_emails: userEmails, updated_at: new Date().toISOString() })
      .eq("flag", flagName);
    if (error) {
      setStatuses((s) => ({ ...s, [flagName]: "error" }));
    } else {
      setStatuses((s) => ({ ...s, [flagName]: "saved" }));
      void refresh();
      setTimeout(() => setStatuses((s) => ({ ...s, [flagName]: "idle" })), 2500);
    }
  }

  if (loading && flags.length === 0) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-neutral-400">
        <svg className="animate-spin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
        </svg>
        Loading flags…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-neutral-200 bg-white p-6">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-semibold text-neutral-800">Feature Flags</h2>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Admin only</span>
        </div>
        <p className="text-xs text-neutral-400 mb-5">Control feature rollout across users. Empty user list = enabled for everyone.</p>

        {flags.length === 0 ? (
          <p className="text-sm text-neutral-400">No flags found in database.</p>
        ) : (
          <div className="space-y-4">
            {flags.map((f) => {
              const d = getDraft(f.flag, f);
              const status = statuses[f.flag] ?? "idle";
              return (
                <div key={f.flag} className="rounded-xl border border-neutral-200 p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <code className="text-sm font-mono font-semibold text-neutral-900">{f.flag}</code>
                      {f.description && (
                        <p className="mt-0.5 text-xs text-neutral-400">{f.description}</p>
                      )}
                    </div>
                    {/* Toggle */}
                    <button
                      onClick={() =>
                        setDrafts((prev) => ({
                          ...prev,
                          [f.flag]: { ...d, enabled: !d.enabled },
                        }))
                      }
                      className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                        d.enabled ? "bg-[#C47A2F]" : "bg-neutral-200"
                      }`}
                      role="switch"
                      aria-checked={d.enabled}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                          d.enabled ? "translate-x-5" : "translate-x-0"
                        }`}
                      />
                    </button>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs text-neutral-500">
                      User emails <span className="text-neutral-400">(comma-separated — leave blank for all users)</span>
                    </label>
                    <input
                      type="text"
                      value={d.userEmailsText}
                      onChange={(e) =>
                        setDrafts((prev) => ({
                          ...prev,
                          [f.flag]: { ...d, userEmailsText: e.target.value },
                        }))
                      }
                      placeholder="user@example.com, other@example.com"
                      className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm text-neutral-800 placeholder-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-300"
                    />
                  </div>

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => void handleSave(f.flag)}
                      disabled={status === "saving"}
                      className="rounded-lg bg-neutral-900 px-4 py-1.5 text-xs font-semibold text-white hover:bg-neutral-700 disabled:opacity-50 transition-colors"
                    >
                      {status === "saving" ? "Saving…" : "Save"}
                    </button>
                    {status === "saved" && (
                      <span className="text-xs text-emerald-600 font-medium">✓ Saved</span>
                    )}
                    {status === "error" && (
                      <span className="text-xs text-red-600 font-medium">Failed — check console</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Promo codes reference card */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-neutral-800">Promo codes</h2>
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700">Admin only</span>
        </div>
        <p className="text-xs text-neutral-400">
          Read-only reference. To activate for users, toggle the <code className="font-mono bg-neutral-100 px-1 rounded">promo_codes</code> feature flag above.
          After creating coupons in Stripe, update the <code className="font-mono bg-neutral-100 px-1 rounded">COUPON_MAP</code> in <code className="font-mono bg-neutral-100 px-1 rounded">stripe-checkout/index.ts</code>.
        </p>
        <div className="space-y-2">
          {[
            { code: "BETA50",    desc: "50% off for 3 months", coupon: "PLACEHOLDER_BETA50_COUPON_ID" },
            { code: "EARLYBIRD", desc: "20% off forever",       coupon: "PLACEHOLDER_EARLYBIRD_COUPON_ID" },
            { code: "FRIEND",    desc: "100% off for 1 month",  coupon: "PLACEHOLDER_FRIEND_COUPON_ID" },
          ].map(({ code, desc, coupon }) => (
            <div key={code} className="flex items-center justify-between rounded-xl border border-neutral-100 bg-neutral-50 px-4 py-2.5 gap-4">
              <div className="flex items-center gap-3">
                <code className="text-sm font-mono font-bold text-neutral-900 tracking-wider">{code}</code>
                <span className="text-sm text-neutral-500">{desc}</span>
              </div>
              <code className="text-xs font-mono text-neutral-400 shrink-0">{coupon}</code>
            </div>
          ))}
        </div>
        <p className="text-xs text-neutral-400">
          Create coupons: Stripe Dashboard → Products → Coupons → Create coupon. Then replace the placeholder IDs above.
        </p>
      </div>
    </div>
  );
}
